import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import firebase from 'firebase/compat/app';
import 'firebase/compat/messaging';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export type PwaPushPayload = {
  id?: string;
  type: string;
  tap?: 'background' | 'foreground';
  d: string;
  room?: string;
  title?: string;
  body?: string;
  Action?: string;
  Path?: string;
  aps?: { alert?: { title?: string; body?: string } };
};

const PUSH_URL_PARAMS = ['pushTap', 'type', 'd', 'room', 'id', 'Action', 'Path', 'title', 'body'] as const;
const PENDING_PUSH_CACHE = 'pwa-pending-push-v1';
const PENDING_PUSH_KEY = '/pending-push';

type FirebaseEnv = {
  vapidKey?: string;
  apiKey: string;
  authDomain: string;
  databaseURL?: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
};

/** Same scope Firebase web SDK uses when auto-registering the messaging SW. */
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

@Injectable({
  providedIn: 'root',
})
export class PwaService {

  private messaging: firebase.messaging.Messaging | null = null;
  private messagingInitPromise: Promise<boolean> | null = null;
  private foregroundMessageSubscribed = false;
  private fcmSwRegistration: ServiceWorkerRegistration | null = null;
  private notificationClickSubject = new Subject<PwaPushPayload>();
  private notificationClickListening = false;
  private lastHandledPushKey: string | null = null;

  constructor(private swUpdate: SwUpdate) {}

  isPwaBuild(): boolean {
    return !!(environment as { pwa?: boolean }).pwa;
  }

  initServiceWorkerUpdates(onUpdateAvailable: () => void): void {
    if (!this.isPwaBuild() || !this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => onUpdateAvailable());

    this.swUpdate.checkForUpdate().catch(err => console.warn('[PWA] checkForUpdate failed', err));
  }

  activateUpdate(): Promise<boolean> {
    return this.swUpdate.activateUpdate();
  }

  initFirebaseMessaging(): Promise<boolean> {
    if (!this.messagingInitPromise) {
      this.messagingInitPromise = this.setupFirebaseMessaging();
    }
    return this.messagingInitPromise;
  }

  private async setupFirebaseMessaging(): Promise<boolean> {
    if (!this.isPwaBuild() || !environment.production) {
      return false;
    }

    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      console.warn('[PWA] Push notifications are not supported in this browser');
      return false;
    }

    const firebaseEnv = (environment as { firebase?: FirebaseEnv }).firebase;
    if (!firebaseEnv) {
      return false;
    }

    try {
      const { vapidKey: _vapidKey, ...appConfig } = firebaseEnv;
      if (!firebase.apps.length) {
        firebase.initializeApp(appConfig);
      }
      this.messaging = firebase.messaging();
      return true;
    } catch (err) {
      console.error('[PWA] initFirebaseMessaging failed', err);
      this.messagingInitPromise = null;
      return false;
    }
  }

  /**
   * Wait until the FCM registration (not ngsw at `/`) has an active worker.
   * navigator.serviceWorker.ready only reflects the page controller (ngsw).
   */
  private waitForServiceWorkerActivation(
    registration: ServiceWorkerRegistration,
    timeoutMs = 30000,
  ): Promise<ServiceWorkerRegistration> {
    if (registration.active) {
      return Promise.resolve(registration);
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('[PWA] FCM service worker activation timed out'));
      }, timeoutMs);

      const finish = () => {
        if (registration.active) {
          clearTimeout(timeout);
          resolve(registration);
        }
      };

      const watchWorker = (worker: ServiceWorker) => {
        if (worker.state === 'activated') {
          finish();
          return;
        }
        worker.addEventListener('statechange', () => {
          if (worker.state === 'activated') {
            finish();
          }
        });
      };

      const existingWorker = registration.installing || registration.waiting;
      if (existingWorker) {
        watchWorker(existingWorker);
        return;
      }

      registration.addEventListener('updatefound', () => {
        const worker = registration.installing;
        if (worker) {
          watchWorker(worker);
        }
      });
    });
  }

  /**
   * Dedicated FCM service worker (separate from Angular ngsw), same pattern as ProBox One web GUI.
   */
  private async prepareFirebaseMessagingRegistration(): Promise<ServiceWorkerRegistration> {
    if (this.fcmSwRegistration?.active) {
      return this.fcmSwRegistration;
    }

    let registration = await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE);
    if (!registration) {
      registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: FCM_SW_SCOPE,
      });
    }

    registration = await this.waitForServiceWorkerActivation(registration);
    this.fcmSwRegistration = registration;
    return registration;
  }

  private async clearFirebaseClientState(): Promise<void> {
    if (this.messaging) {
      try {
        await this.messaging.deleteToken();
      } catch (err) {
        console.warn('[PWA] deleteToken failed', err);
      }
    }

    if (typeof indexedDB !== 'undefined' && indexedDB.databases) {
      try {
        const databases = await indexedDB.databases();
        for (const db of databases) {
          if (db.name && /firebase/i.test(db.name)) {
            indexedDB.deleteDatabase(db.name);
          }
        }
      } catch (err) {
        console.warn('[PWA] clear firebase indexedDB failed', err);
      }
    }

    this.fcmSwRegistration = null;
    this.messaging = null;
    this.messagingInitPromise = null;
  }

  async requestPermissionAndGetToken(): Promise<string | null> {
    const ready = await this.initFirebaseMessaging();
    if (!ready || !this.messaging) {
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('[PWA] Notification permission not granted');
      return null;
    }

    const firebaseEnv = (environment as { firebase?: FirebaseEnv }).firebase;
    const maxAttempts = 3;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const registration = await this.prepareFirebaseMessagingRegistration();
        const tokenOptions: { serviceWorkerRegistration: ServiceWorkerRegistration; vapidKey?: string } = {
          serviceWorkerRegistration: registration,
        };
        if (firebaseEnv?.vapidKey) {
          tokenOptions.vapidKey = firebaseEnv.vapidKey;
        }

        const token = await this.messaging.getToken(tokenOptions);
        if (token) {
          console.log('[PWA] FCM token obtained');
          return token;
        }
      } catch (err: unknown) {
        const code = (err as { code?: string })?.code;
        const message = String((err as Error)?.message || err);
        const isSubscribeFailed = code === 'messaging/token-subscribe-failed';
        const isSwNotReady = message.includes('no active Service Worker')
          || (err instanceof DOMException && err.name === 'AbortError');
        console.error(`[PWA] getToken attempt ${attempt}/${maxAttempts} failed`, err);

        if ((isSubscribeFailed || isSwNotReady) && attempt < maxAttempts) {
          if (isSubscribeFailed) {
            await this.clearFirebaseClientState();
            await this.initFirebaseMessaging();
          } else {
            this.fcmSwRegistration = null;
          }
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }

        if (isSubscribeFailed) {
          console.error(
            '[PWA] FCM 401 — kiểm tra Google Cloud Console cho API key ' +
            `${firebaseEnv?.apiKey?.slice(0, 12)}…: ` +
            '(1) bật FCM Registration API + Firebase Installations API, ' +
            '(2) API key không restrict referrer chặn domain này, ' +
            '(3) clear site data rồi thử lại.'
          );
        }
        return null;
      }
    }

    return null;
  }

  private parsePushPayload(data: Record<string, string>, tap: 'background' | 'foreground'): PwaPushPayload {
    const type = data.type || (data.room ? 'CHATROOM' : '');
    return {
      id: data.id,
      type,
      tap,
      d: data.d,
      room: data.room,
      title: data.title,
      body: data.body,
      Action: data.Action,
      Path: data.Path,
    };
  }

  /** iOS PWA often opens start_url without postMessage — read payload SW persisted in Cache API. */
  async consumePendingPushFromCache(): Promise<PwaPushPayload | null> {
    if (!this.isPwaBuild() || typeof caches === 'undefined') {
      return null;
    }

    try {
      const cache = await caches.open(PENDING_PUSH_CACHE);
      const response = await cache.match(PENDING_PUSH_KEY);
      if (!response) {
        return null;
      }

      await cache.delete(PENDING_PUSH_KEY);
      const data = JSON.parse(await response.text()) as Record<string, string>;
      if (!data.type) {
        return null;
      }
      return this.parsePushPayload(data, 'background');
    } catch (err) {
      console.warn('[PWA] consumePendingPushFromCache failed', err);
      return null;
    }
  }

  /** Register listeners early so iOS cold-start/resume does not miss the tap event. */
  startNotificationClickHandling(): void {
    if (!this.isPwaBuild() || this.notificationClickListening) {
      return;
    }
    this.notificationClickListening = true;

    const swMessageHandler = (event: MessageEvent) => {
      if (event.data?.type !== 'PUSH_NOTIFICATION_CLICK') {
        return;
      }
      const data = (event.data.data || {}) as Record<string, string>;
      this.emitNotificationClick(this.parsePushPayload(data, 'background'));
    };

    navigator.serviceWorker.addEventListener('message', swMessageHandler);

    const recheckPendingPush = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') {
        return;
      }
      this.emitPendingPushNavigation().catch(err => console.warn('[PWA] emitPendingPushNavigation failed', err));
    };

    window.addEventListener('pageshow', recheckPendingPush);
    document.addEventListener('visibilitychange', recheckPendingPush);
    recheckPendingPush();
  }

  /** Push payload from cold-start URL after notification opened a new window. */
  consumePushNavigationFromUrl(): PwaPushPayload | null {
    if (!this.isPwaBuild()) {
      return null;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('pushTap') !== 'background' || !params.get('type')) {
      return null;
    }

    const data: Record<string, string> = {};
    PUSH_URL_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        data[key] = value;
      }
    });

    const url = new URL(window.location.href);
    PUSH_URL_PARAMS.forEach(key => url.searchParams.delete(key));
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);

    return this.parsePushPayload(data, 'background');
  }

  /** Background notification tap: SW postMessage, Cache API (iOS), or cold-start URL params. */
  onNotificationClick(): Observable<PwaPushPayload> {
    return this.notificationClickSubject.asObservable();
  }

  private getPushDedupeKey(data: PwaPushPayload): string {
    return [data.type, data.room || '', data.Path || '', data.id || '', data.d || ''].join('|');
  }

  private emitNotificationClick(data: PwaPushPayload): void {
    if (!data.type) {
      return;
    }
    const key = this.getPushDedupeKey(data);
    if (key === this.lastHandledPushKey) {
      return;
    }
    this.lastHandledPushKey = key;
    this.notificationClickSubject.next(data);
  }

  private async emitPendingPushNavigation(): Promise<void> {
    const fromUrl = this.consumePushNavigationFromUrl();
    if (fromUrl) {
      this.emitNotificationClick(fromUrl);
      return;
    }

    const fromCache = await this.consumePendingPushFromCache();
    if (fromCache) {
      this.emitNotificationClick(fromCache);
    }
  }

  onForegroundMessage(): Observable<PwaPushPayload> {
    return new Observable(observer => {
      if (!this.messaging) {
        observer.complete();
        return;
      }

      if (this.foregroundMessageSubscribed) {
        return;
      }
      this.foregroundMessageSubscribed = true;

      const unsubscribe = this.messaging.onMessage((payload) => {
        const data = (payload.data || {}) as Record<string, string>;
        observer.next({
          ...this.parsePushPayload(data, 'foreground'),
          title: payload.notification?.title || data.title,
          body: payload.notification?.body || data.body,
        });
      });

      return () => {
        this.foregroundMessageSubscribed = false;
        unsubscribe();
      };
    });
  }
}
